import { useState } from "react";
import { useNavigate } from "react-router-dom";
import TextInput from "../forms/TextInput";
import { useAuth } from "../../js/hooks/useAuth";
import { validateLogin, validateBuyerAccount, validateSellerAccount, validateAgentAccount, validateBrokerAccount } from "../../js/utils/validators";
import { defaultSeatTierId } from "../../js/auth/packageAccess";
import { SEAT_TIERS } from "../../js/utils/siteLinks";
import { resolvePostAuthPath } from "../../js/auth/onboardingState";
import { toTitleCase } from "../../js/utils/titleCase";

const ROLES = [
  { id: "buyer", label: "Buyer" },
  { id: "seller", label: "Seller" },
];

const EMPTY_REGISTER = {
  name: "",
  email: "",
  phone: "",
  password: "",
  agent: "",
  broker: "",
  office: "",
  tier: defaultSeatTierId(),
};

export default function SignInPanel({ onSignedIn, compact = false }) {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [view, setView] = useState("signin");
  const [role, setRole] = useState("buyer");
  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [registerForm, setRegisterForm] = useState(EMPTY_REGISTER);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function updateRegister(event) {
    const { name, value } = event.target;
    setRegisterForm((current) => ({ ...current, [name]: value }));
  }

  function switchRole(nextRole) {
    setRole(nextRole);
    setErrors({});
    setMessage("");
    setForm({ email: "", password: "" });
  }

  function showSignIn() {
    setView("signin");
    setErrors({});
    setMessage("");
  }

  function showRegister() {
    setView("register");
    setErrors({});
    setMessage("");
  }

  async function handleSignIn(event) {
    event.preventDefault();
    const nextErrors = validateLogin(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    try {
      const session = await login(form, role);
      if (onSignedIn) onSignedIn(session, role);
      else navigate(resolvePostAuthPath(session, role));
    } catch (error) {
      setMessage(error.message);
    }
  }

  function validateRegister() {
    if (role === "agent") return validateAgentAccount(registerForm);
    if (role === "broker") return validateBrokerAccount(registerForm);
    if (role === "seller") return validateSellerAccount(registerForm);
    return validateBuyerAccount(registerForm);
  }

  async function handleRegister(event) {
    event.preventDefault();
    const nextErrors = validateRegister();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    try {
      const result = await register({ ...registerForm, role });
      if (onSignedIn) onSignedIn(result.session, role);
      else navigate(resolvePostAuthPath(result.session, role));
    } catch (error) {
      setMessage(error.message);
    }
  }

  return (
    <div className={compact ? "auth-card" : "card auth-card"}>
      {compact ? null : <h1>{toTitleCase(view === "signin" ? "Sign in" : "New here?")}</h1>}
      <p className="page-lead">
        {view === "signin"
          ? compact
            ? "Continue as a buyer or seller. New Here? is below."
            : "Sign in to continue your buyer or seller journey. New Here? stays at the bottom."
          : compact
            ? "Buyers and sellers stay free."
            : "Create an account only after you have seen Sign in. Buyers and sellers stay free."}
      </p>
      <nav className="auth-tabs messages-desk__tabs" aria-label="Account type">
        {ROLES.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`messages-tab${role === item.id ? " is-active" : ""}`}
            onClick={() => switchRole(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {view === "signin" ? (
        <form onSubmit={handleSignIn} autoComplete="off">
          <div className="auth-signin-row">
            <TextInput
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={update}
              error={errors.email}
              autoComplete="username"
            />
            <TextInput
              label="Password"
              name="password"
              type="password"
              value={form.password}
              onChange={update}
              error={errors.password}
              autoComplete="current-password"
            />
          </div>
          {message ? <p className="field-error">{message}</p> : null}
          <button type="submit" className="primary-btn btn-block">
            Sign in
          </button>
          <p className="auth-switch muted">
            <button type="button" className="text-btn" onClick={showRegister}>
              New Here?
            </button>
          </p>
        </form>
      ) : (
        <form onSubmit={handleRegister}>
          <TextInput label="Full name" name="name" value={registerForm.name} onChange={updateRegister} error={errors.name} />
          <TextInput
            label="Email"
            name="email"
            type="email"
            value={registerForm.email}
            onChange={updateRegister}
            error={errors.email}
          />
          {role === "buyer" || role === "seller" || role === "agent" ? (
            <TextInput label="Phone" name="phone" value={registerForm.phone} onChange={updateRegister} error={errors.phone} />
          ) : null}
          <TextInput
            label="Password"
            name="password"
            type="password"
            value={registerForm.password}
            onChange={updateRegister}
            autoComplete="new-password"
          />
          {role === "agent" || role === "buyer" || role === "seller" ? (
            <TextInput
              label={role === "agent" ? "Broker" : "Your agent"}
              name={role === "agent" ? "broker" : "agent"}
              value={role === "agent" ? registerForm.broker : registerForm.agent}
              onChange={updateRegister}
              error={role === "agent" ? errors.broker : errors.agent}
            />
          ) : null}
          {role === "buyer" || role === "seller" ? (
            <TextInput
              label="Your broker"
              name="broker"
              value={registerForm.broker}
              onChange={updateRegister}
              error={errors.broker}
            />
          ) : null}
          <TextInput label="Office" name="office" value={registerForm.office} onChange={updateRegister} error={errors.office} />
          {role === "broker" ? (
            <label className="field">
              <span className="field-label">Seat plan</span>
              <select className="field-select" name="tier" value={registerForm.tier} onChange={updateRegister}>
                {Object.values(SEAT_TIERS).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {message ? <p className="field-error">{message}</p> : null}
          <button type="submit" className="primary-btn btn-block">
            Create account
          </button>
          <p className="auth-switch muted">
            Already have an account?{" "}
            <button type="button" className="text-btn" onClick={showSignIn}>
              Sign In
            </button>
          </p>
        </form>
      )}
    </div>
  );
}
